import { useEffect, useState } from 'react'
import type { EntityRecord } from '@common/entities'
import type { EntitiesCapability, EntitySelector, Page } from '@common/plugin-host'
import type { SnapshotEntity } from 'runtimes-shared'
import getEntities from '../io/api/entity/getEntities'
import getEntitiesById from '../io/api/entity/getEntitiesById'

const PREVIEW_LIMIT = 200

// The value before the request answers: `loading` is what tells it from a genuinely empty result.
const LOADING: Page<EntityRecord> = { items: [], loading: true }

const asRecord = (entity: SnapshotEntity): EntityRecord => entity as EntityRecord

const select = async (selector: EntitySelector): Promise<readonly EntityRecord[]> => {
  switch (selector.type) {
    case 'OfType':
      return (await getEntities(selector.typeId, { limit: PREVIEW_LIMIT })).map(asRecord)
    case 'Single':
      return (await getEntitiesById([selector.entityId])).map(asRecord)
    case 'None':
      return []
    default:
      throw new Error(`A console preview cannot answer the entity selector '${selector.type}'.`)
  }
}

export const serverEntities = (): EntitiesCapability => {
  const useSelection = <T>(read: () => Promise<T>, initial: T, key: string): T => {
    const [value, setValue] = useState<T>(initial)
    useEffect(() => {
      let active = true
      void read()
        .then((next) => {
          if (active) setValue(next)
        })
        .catch((error: unknown) => console.error('[entities] the entity service could not be read', error))
      return () => {
        active = false
      }
    }, [key])
    return value
  }

  return {
    reader: {
      getEntity: async (entityId) => (await getEntitiesById([entityId])).map(asRecord)[0],

      getEntities: async (selector) => ({ items: await select(selector) }),

      useEntity: (entityId) =>
        useSelection<EntityRecord | undefined>(
          async () => (await getEntitiesById([entityId])).map(asRecord)[0],
          undefined,
          entityId
        ),

      useEntities: (selector) =>
        useSelection<Page<EntityRecord>>(
          async () => ({ items: await select(selector) }),
          LOADING,
          JSON.stringify(selector)
        ),
    },
  }
}

export default serverEntities
